/**
 * 用户数据服务
 * 处理用户相关的API请求
 */

import api from './api';
import endpoints from './endpoints';
import { getCurrentUser as getAuthCurrentUser } from './authService';
// 将本地缓存格式转换为符合 API 文档的格式
const convertToApiFormat = (user) => {
  if (!user) return null;

  // 创建符合 API 文档中 User 消息类型的格式
  return {
    id: user.id,
    email: user.email,
    name: user.name || user.username,
    workspace: user.workspace || 'default',
    vip: user.vip || false,
    avatar: user.avatar || null,
    phone: user.phone || '',
    role: user.role || 'user',
    preferences: user.preferences || {},
    last_login: user.last_login || { seconds: Math.floor(Date.now() / 1000) },
    inserted_at: user.inserted_at || { seconds: Math.floor(Date.now() / 1000) },
    updated_at: user.updated_at || { seconds: Math.floor(Date.now() / 1000) },
  };
};

const userService = {
  /**
   * 获取当前用户信息
   * @returns {Promise} - 当前用户数据
   */
  getCurrentUser: async () => {
    try {
      const localUser = getAuthCurrentUser();
      const userId = localUser && localUser.id;
      if (!userId) {
        throw new Error('未找到本地用户ID');
      }

      const response = await api.get(endpoints.users.detail(userId));
      if (response && response.id) {
        // 确保 name 字段存在，如果不存在则使用 username
        if (!response.name && response.username) {
          response.name = response.username;
        }
        return response;
      }
      throw new Error('未获取到有效的用户详情数据');
    } catch (error) {
      console.warn('获取当前用户信息失败，尝试使用本地缓存', error);
      const fallbackUser = getAuthCurrentUser();
      if (fallbackUser && fallbackUser.id) {
        return convertToApiFormat(fallbackUser);
      }
      throw error;
    }
  },

  /**
   * 获取用户列表
   * @param {Object} params - 查询参数
   * @returns {Promise} - 用户列表数据
   */
  getUsers: async (params = {}) => {
    try {
      const response = await api.get(endpoints.users.list, { params });

      if (Array.isArray(response)) {
        return response.map(convertToApiFormat);
      }

      if (response && response.users && Array.isArray(response.users)) {
        return response.users.map(convertToApiFormat);
      }

      if (response && response.data && Array.isArray(response.data)) {
        return response.data.map(convertToApiFormat);
      }

      return [];
    } catch (error) {
      console.error('获取用户列表失败:', error);
      return [];
    }
  },

  /**
   * 获取用户详情
   * @param {string} id - 用户ID
   * @returns {Promise} - 用户详情数据
   */
  getUserDetail: async (id) => {
    try {
      const response = await api.get(endpoints.users.detail(id));
      console.log('getUserDetail response', response);
      if (response && response.id) {
        return response;
      }
      // 如果没有获取到用户详情，直接抛出异常
      throw new Error('未获取到有效的用户详情数据');
    } catch (error) {
      console.error(`获取用户详情失败 (ID: ${id}):`, error);
      // 发生错误时直接抛出异常，由调用方决定如何处理
      throw error;
    }
  },

  // 获取个人信息
  getPersonalInfo: async (userId) => {
    try {
      const currentUser = getAuthCurrentUser();
      const targetUserId = userId || currentUser?.id;

      if (!targetUserId) {
        throw new Error('无法获取用户ID');
      }

      const response = await api.get(
        endpoints.users.detail(targetUserId),
        {},
        'GetUserDetailResponse'
      );

      return response;
    } catch (error) {
      console.error('获取个人信息失败:', error);
      return {
        followers: '0',
        following: '0',
        bio: '暂无简介',
        tag: [],
        like_count: '0',
        badge: '0',
        certificate: '0',
        shop: '0',
        agent: '0',
        template: '0',
      };
    }
  },

  getFriends: async (params = {}) => {
    const requestParams = {
      scope: 'search',
      filter: params.filter,
      sort: params.sort,
      pagination: {
        page: params.page || 1,
        per_page: params.per_page || 20,
      },
    };

    const response = await api.post(endpoints.friends.search, requestParams);
    return {
      users: response.cards || [],
      pagination: response.pagination || { total: 0, page: 1, per_page: 20 },
    };
  },

  /**
   * 获取用户个人帖子列表 (API规范: POST /v1/syntrust/post/search)
   * @param {Object} params - 查询参数
   * @returns {Promise} - 个人帖子列表数据
   */
  getUserPosts: async (params = {}) => {
    try {
      console.log('获取个人帖子列表，参数:', params);

      // 构建符合content search API规范的请求参数
      const requestParams = {
          type: 'biography', // 个人主页使用biography类型
        filter: {
          exprs: [
            {
              field: 'author_id',
              op: 'EQ',
              values: [params.user_id]
            }
          ]
        },
        sort: params.sort || { field: 'inserted_at', desc: true }, // 默认按创建时间降序
        pagination: params.pagination || {
          page: 1,
          per_page: 20,
        },
      };

      // 如果有额外的筛选条件，合并到filter中
      if (params.filter && params.filter.exprs) {
        requestParams.filter.exprs = [...requestParams.filter.exprs, ...params.filter.exprs];
      }

      console.log('发送的请求参数:', requestParams);

      // 调用统一内容搜索API
      const response = await api.post(
        endpoints.competition.search, // 使用统一内容搜索端点
        requestParams
      );

      console.log('个人帖子列表获取成功:', response);
      
      // 返回兼容格式，将contents映射为cards
      return {
        pagination: response.pagination || {
          total: 0,
          page: 1,
          per_page: 20,
        },
        cards: response.contents || [],
      };
    } catch (error) {
      console.error('获取个人帖子列表失败:', error);
      throw error;
    }
  },

  /**
   * 获取用户社交媒体链接
   * @param {string} userId - 用户ID，默认当前用户
   * @returns {Promise<Array>} - 社交媒体链接数组
   */
  getUserSocialLinks: async (userId) => {
    try {
      let targetUserId = userId;
      if (!targetUserId) {
        const currentUser = getAuthCurrentUser();
        targetUserId = currentUser?.id;
      }
      if (!targetUserId) throw new Error('无法获取用户ID');
      // 使用 endpoints 中定义的端点
      const endpoint = endpoints.users.socialLinks.list(targetUserId);
      const res = await api.get(endpoint);
      console.log('getUserSocialLinks-res', res);
      // 处理API响应格式 - 严格按照API规范
      if (
        res &&
        res.success &&
        res.data &&
        Array.isArray(res.data.social_links)
      ) {
        return res.data.social_links;
      }

      // 兼容直接返回数组的情况
      if (Array.isArray(res)) {
        return res;
      }

      // 兼容其他响应格式
      if (res && Array.isArray(res.social_links)) {
        return res.social_links;
      }

      return [];
    } catch (e) {
      console.error('获取社交媒体链接失败', e);
      return [];
    }
  },

  updateUserInfo: async (userId, data) => {
    console.log('updateUserInfo', userId, data);
    try {
      const response = await api.put(`/users/${userId}`, data, '', '', { deferBusinessErrorMessage: true });
      return response;
    } catch (error) {
      // 将用户名重复的错误映射为更友好的文案
      try {
        const details = error?.details || error?.data || error?.errors || null;
        const usernameErrors = details && details.username;
        if (Array.isArray(usernameErrors)) {
          const taken = usernameErrors.some((m) => /has already been taken/i.test(m));
          if (taken) {
            const msg = '用户名已被占用';
            // 同步提示
            window.message?.error?.(msg);
            const e = new Error(msg);
            e.code = 3000;
            e.details = details;
            throw e;
          }
        }
      } catch (_) {}
      window.message?.error?.(error?.message || '请求失败');
      throw error;
    }
  },

  /**
   * 上传用户头像
   * @param {string} userId
   * @param {File} file
   * @returns {Promise<{avatar_url: string, avatar_id: string}>}
   */
  uploadAvatar: async (userId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_name', file.name);
    formData.append('content_type', 'image');
    formData.append('alt_text', 'avatar');
    formData.append('status', 'published');
    // POST /users/{user_id}/avatar
    const res = await api.upload(`/users/${userId}/avatar`, formData);
    if (res && res.data && res.data.avatar_url) {
      return res.data;
    }
    if (res && res.avatar_url) {
      return res;
    }
    throw new Error('头像上传失败');
  },

  /**
   * 搜索用户
   * @param {Object} params - 搜索参数
   * @param {string} params.name - 搜索关键词（姓名）
   * @param {string} params.email - 搜索关键词（邮箱）
   * @param {number} params.limit - 返回结果数量，默认10，最大50
   * @returns {Promise} - 搜索结果
   */
  searchUsers: async (params = {}) => {
    try {
      const response = await api.get(endpoints.users.search, { params });
      return response;
    } catch (error) {
      console.error("搜索用户失败:", error);
      throw error;
    }
  },
};

export default userService;
